import { type AnalyticsService } from '@/shared/services/analytics/analytics-service';

import { type Pageable, type PageableRes } from '../../types/common';

export enum GENRES {
  PUZZLE = 'puzzle',
  CARD = 'card',
  FOR_GIRLS = 'for_girls',
  MATCH3 = 'match3',
  FARM = 'farm',
  WITH_FRIENDS = 'with_friends',
  STRATEGY = 'strategy',
  QUICK = 'quick',
  FOR_BOYS = 'for_boys',
  CASUAL = 'casual',
  CRAZY = 'crazy',
  SPORT = 'sport',
  ARCADE = 'arcade',
}

export enum CATEGORY {
  NEW = 'new',
  RECENT = 'recent',
  FAVORITE = 'favorite',
  SANDBOX = 'sandbox',
  POPULAR = 'popular',
  PROMOTION = 'promotion',
  MORE_PLAYCOIN = '2lorjfpzix',
}

export type CategoryGames = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [key in CATEGORY | (string & {})]: {
    content: Game[];
    pageable: Pageable;
  };
};

export interface Selection {
  n: number;
  color: 'green' | 'orange' | 'blue' | 'pink' | 'purple' | null;
  selectionType: 'CORE' | 'GENRE' | 'CUSTOM';
  selectionKey: string;
  title: string;
  icon: string;
  content: Game[];
}

export interface GameRating {
  rating: number;
  count: number;
  uniquePlayers: number;
}

export interface Game {
  title: string;
  description: string;
  mpDescription: string;
  iconUrl: string;
  wideCover?: string | null;
  gameUrl: string;
  key: string;
  promo?: {
    url: string;
    background: string | null;
    size: 'small' | 'medium' | 'large';
  };
  preloader?: string | null;
  preloaderBackground?: string | null;
  favorite: boolean;
  sandbox: boolean;
  tags: string[];
  genres: Genre[];
  stories?: StoryContent;

  rating?: GameRating;
  screenshots?: string[];
  flags: Partial<{
    webapp: boolean;
    online: boolean;
    bot: boolean;
    promoted: boolean;
    fullscreen: boolean;
    chosen: boolean;
    new: boolean;
    adult: boolean;
  }>;
}

export type ExtendedGame = Game & {
  cover?: string;
  animatedCover?: string;
};

interface StoryContent {
  cover: string | null;
  video: string | null;
  duration: number | null;
}

export interface Genre {
  id: number;
  key: string;
  icon: string;
  name: string;
}

export interface RawGenre {
  id: number;
  key: string;
  icon: string;
  name: string;
  nameAccusative: string;
}

export interface ParsedGenre {
  id: number;
  key: string;
  icon: string;
  name: GenreLocalization | null;
  nameAccusative: GenreLocalization | null;
}

export interface GenreLocalization {
  ru: string;
  en: string;
}

export interface ImpressionContext {
  feedScrollPage: number;
  visibleGameKeys: string[];
}

export interface GamesActions {
  hydrate: (fresh: Partial<GamesState>) => void;
  fetchSelections: () => Promise<void>;
  fetchGenres: () => Promise<void>;
  fetchGamesByCategory: (
    page: number,
    // eslint-disable-next-line @typescript-eslint/ban-types
    category: CATEGORY | (string & {}),
    size?: number
  ) => Promise<PageableRes<Game[]> | undefined>;
  playClick: (
    game: Pick<Game, 'key' | 'gameUrl' | 'flags' | 'title'>,
    placement: string,
    analyticsService: AnalyticsService,
    placementNum?: number,
    impressionContext?: ImpressionContext
  ) => Promise<void>;
}

export type LastFetchedPages = {
  [key in CATEGORY]: number;
};

export interface GamesState {
  categoryGames: CategoryGames | null;
  genres: ParsedGenre[] | undefined;
  actions: GamesActions;
  lastFetchedPages: LastFetchedPages;
  selections: Selection[] | undefined;
  selectionsByKey: Record<string, Selection> | undefined;
}
